import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function SitemapPage() {
  useEffect(() => {
    document.title = 'HTML Sitemap | Hoosier Life Insurance - All Pages';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'HTML sitemap of Hoosier Life Insurance. Browse all pages including life insurance products, Indiana service areas, resources & contact information.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'sitemap, site map, all pages, life insurance Indiana, service areas, insurance products');
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://hoosierlifeinsurance.com/sitemap');

    const baseUrl = 'https://hoosierlifeinsurance.com';
    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${baseUrl}/sitemap#webpage`,
          "url": `${baseUrl}/sitemap`,
          "name": "HTML Sitemap | Hoosier Life Insurance",
          "description": "Complete HTML sitemap of Hoosier Life Insurance website.",
          "isPartOf": { "@type": "WebSite", "@id": `${baseUrl}/#website`, "name": "Hoosier Life Insurance", "url": baseUrl },
          "breadcrumb": { "@id": `${baseUrl}/sitemap#breadcrumb` }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${baseUrl}/sitemap#breadcrumb`,
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${baseUrl}/` },
            { "@type": "ListItem", "position": 2, "name": "Sitemap", "item": `${baseUrl}/sitemap` }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'sitemap-page-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.getElementById('sitemap-page-schema')?.remove();
    };
  }, []);

  // ── All link groups — using plain <a href> so raw HTML crawlers can follow them ──

  const corePages = [
    { href: '/', label: 'Home' },
    { href: '/about', label: 'About Us' },
    { href: '/contact', label: 'Contact Us' },
    { href: '/faq', label: 'Frequently Asked Questions' },
    { href: '/all-services', label: 'All Insurance Services' },
    { href: '/book-appointment', label: 'Book a Free Appointment' },
    { href: '/instant-quote', label: 'Get an Instant Quote' },
    { href: '/wealth-strategy-call', label: 'Free Wealth Strategy Call' },
    { href: '/quiz', label: 'Life Insurance Quiz' },
    { href: '/compare', label: 'Compare Insurance Products' },
    { href: '/privacy-policy', label: 'Privacy Policy' },
    { href: '/terms-of-service', label: 'Terms of Service' },
  ];

  const productPages = [
    { href: '/term-life', label: 'Term Life Insurance' },
    { href: '/whole-life', label: 'Whole Life Insurance' },
    { href: '/final-expense', label: 'Final Expense Insurance' },
    { href: '/mortgage-protection', label: 'Mortgage Protection Insurance' },
    { href: '/annuities', label: 'Annuities & Retirement Income' },
    { href: '/indexed-universal-life', label: 'Indexed Universal Life Insurance (IUL)' },
    { href: '/universal-life', label: 'Universal Life Insurance' },
    { href: '/no-medical-exam', label: 'No Medical Exam Life Insurance' },
    { href: '/term-vs-whole-life', label: 'Term vs Whole Life Comparison' },
    { href: '/wealth-building', label: 'Wealth Building Strategies' },
    { href: '/generational-wealth', label: 'Generational Wealth Planning' },
  ];

  const locationPages = [
    { href: '/life-insurance-indiana', label: 'Life Insurance Indiana (Statewide)' },
    { href: '/life-insurance-indianapolis', label: 'Life Insurance Indianapolis' },
    { href: '/life-insurance-fort-wayne', label: 'Life Insurance Fort Wayne' },
    { href: '/life-insurance-south-bend', label: 'Life Insurance South Bend' },
    { href: '/life-insurance-evansville', label: 'Life Insurance Evansville' },
    { href: '/life-insurance-fishers', label: 'Life Insurance Fishers IN' },
    { href: '/life-insurance-noblesville', label: 'Life Insurance Noblesville IN' },
    { href: '/life-insurance-carmel', label: 'Life Insurance Carmel IN' },
    { href: '/life-insurance-hamilton-county', label: 'Life Insurance Hamilton County' },
    { href: '/term-life-indianapolis', label: 'Term Life Indianapolis' },
    { href: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis' },
    { href: '/mortgage-protection-insurance-indianapolis', label: 'Mortgage Protection Insurance Indianapolis' },
  ];

  const specialtyPages = [
    { href: '/life-insurance-comparison-indianapolis', label: 'Life Insurance Comparison Indianapolis' },
    { href: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis' },
    { href: '/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis' },
    { href: '/life-insurance-for-families-indianapolis', label: 'Life Insurance for Families Indianapolis' },
    { href: '/life-insurance-for-diabetics-indianapolis', label: 'Life Insurance for Diabetics Indianapolis' },
    { href: '/life-insurance-for-new-parents-indianapolis', label: 'Life Insurance for New Parents Indianapolis' },
    { href: '/life-insurance-for-self-employed-indianapolis', label: 'Life Insurance for Self-Employed Indianapolis' },
    { href: '/life-insurance-for-veterans-indianapolis', label: 'Life Insurance for Veterans Indianapolis' },
    { href: '/life-insurance-for-seniors-over-60-indianapolis', label: 'Life Insurance for Seniors Over 60 Indianapolis' },
    { href: '/life-insurance-smokers-indianapolis', label: 'Life Insurance for Smokers Indianapolis' },
    { href: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis' },
    { href: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis' },
    { href: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis' },
    { href: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis' },
    { href: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis' },
    { href: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Insurance Quotes Indianapolis' },
    { href: '/term-life-insurance-rates-indianapolis', label: 'Term Life Insurance Rates Indianapolis' },
    { href: '/whole-life-insurance-indianapolis', label: 'Whole Life Insurance Indianapolis' },
    { href: '/whole-life-insurance-quotes-indianapolis', label: 'Whole Life Insurance Quotes Indianapolis' },
    { href: '/final-expense-insurance-indianapolis', label: 'Final Expense Insurance Indianapolis' },
    { href: '/final-expense-insurance-quotes-indianapolis', label: 'Final Expense Insurance Quotes Indianapolis' },
    { href: '/burial-insurance-indianapolis', label: 'Burial Insurance Indianapolis' },
    { href: '/senior-life-insurance-indianapolis', label: 'Senior Life Insurance Indianapolis' },
    { href: '/guaranteed-issue-life-insurance-indianapolis', label: 'Guaranteed Issue Life Insurance Indianapolis' },
    { href: '/high-risk-life-insurance-indianapolis', label: 'High Risk Life Insurance Indianapolis' },
    { href: '/group-life-insurance-indianapolis', label: 'Group Life Insurance Indianapolis' },
    { href: '/business-life-insurance-indianapolis', label: 'Business Life Insurance Indianapolis' },
    { href: '/mortgage-life-insurance-indianapolis', label: 'Mortgage Life Insurance Indianapolis' },
    { href: '/mortgage-protection-indianapolis', label: 'Mortgage Protection Indianapolis' },
  ];

  const serviceAreas = [
    { href: '/service-areas', label: 'All Service Areas Hub' },
    { href: '/service-areas/marion-county', label: 'Marion County' },
    { href: '/service-areas/indianapolis', label: 'Indianapolis' },
    { href: '/service-areas/carmel', label: 'Carmel' },
    { href: '/service-areas/fishers', label: 'Fishers' },
    { href: '/service-areas/noblesville', label: 'Noblesville' },
    { href: '/service-areas/westfield', label: 'Westfield' },
    { href: '/service-areas/zionsville', label: 'Zionsville' },
    { href: '/service-areas/whitestown', label: 'Whitestown' },
    { href: '/service-areas/greenwood', label: 'Greenwood' },
    { href: '/service-areas/avon', label: 'Avon' },
    { href: '/service-areas/plainfield', label: 'Plainfield' },
    { href: '/service-areas/brownsburg', label: 'Brownsburg' },
    { href: '/service-areas/mooresville', label: 'Mooresville' },
    { href: '/service-areas/lebanon', label: 'Lebanon' },
    { href: '/service-areas/beech-grove', label: 'Beech Grove' },
    { href: '/service-areas/speedway', label: 'Speedway' },
    { href: '/service-areas/lawrence', label: 'Lawrence' },
    { href: '/service-areas/castleton', label: 'Castleton' },
    { href: '/service-areas/geist', label: 'Geist' },
    { href: '/service-areas/broad-ripple', label: 'Broad Ripple' },
    { href: '/service-areas/anderson', label: 'Anderson' },
    { href: '/service-areas/muncie', label: 'Muncie' },
    { href: '/service-areas/columbus', label: 'Columbus' },
    { href: '/service-areas/bloomington', label: 'Bloomington' },
    { href: '/service-areas/terre-haute', label: 'Terre Haute' },
    { href: '/service-areas/kokomo', label: 'Kokomo' },
    { href: '/service-areas/richmond', label: 'Richmond' },
    { href: '/service-areas/fort-wayne', label: 'Fort Wayne' },
    { href: '/service-areas/franklin', label: 'Franklin' },
    { href: '/service-areas/greenfield', label: 'Greenfield' },
    { href: '/service-areas/danville', label: 'Danville' },
    { href: '/service-areas/martinsville', label: 'Martinsville' },
    { href: '/service-areas/cumberland', label: 'Cumberland' },
  ];

  const blogArticles = [
    { href: '/blog', label: 'Blog Index — All Articles' },
    { href: '/blog/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis Guide' },
    { href: '/blog/whole-life-insurance-indianapolis', label: 'Whole Life Insurance Indianapolis' },
    { href: '/blog/indexed-universal-life-indianapolis', label: 'Indexed Universal Life Indianapolis' },
    { href: '/blog/universal-life-insurance-indianapolis', label: 'Universal Life Insurance Indianapolis' },
    { href: '/blog/final-expense-insurance-indianapolis', label: 'Final Expense Insurance Indianapolis' },
    { href: '/blog/annuities-retirement-indianapolis', label: 'Annuities & Retirement Indianapolis' },
    { href: '/blog/mortgage-protection-indianapolis', label: 'Mortgage Protection Indianapolis' },
    { href: '/blog/best-life-insurance-indianapolis', label: 'Best Life Insurance in Indianapolis' },
    { href: '/blog/life-insurance-diabetes-indianapolis', label: 'Life Insurance with Diabetes Indianapolis' },
    { href: '/blog/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis' },
    { href: '/blog/life-insurance-seniors-over-60-indianapolis', label: 'Life Insurance for Seniors Over 60 Indianapolis' },
    { href: '/blog/life-insurance-new-parents-indianapolis', label: 'Life Insurance for New Parents Indianapolis' },
    { href: '/blog/term-vs-whole-life-indianapolis', label: 'Term vs Whole Life Indianapolis' },
    { href: '/blog/business-key-person-insurance-indianapolis', label: 'Business Key Person Insurance Indianapolis' },
    { href: '/blog/life-insurance-high-blood-pressure', label: 'Life Insurance with High Blood Pressure' },
    { href: '/blog/life-insurance-private-pilots', label: 'Life Insurance for Private Pilots Indiana' },
    { href: '/blog/wealth-building-life-insurance-indianapolis', label: 'Wealth Building Life Insurance Indianapolis' },
    { href: '/blog/how-much-life-insurance-indiana', label: 'How Much Life Insurance Do I Need in Indiana?' },
    { href: '/blog/cheapest-life-insurance-indiana', label: 'Cheapest Life Insurance Indiana' },
    { href: '/blog/life-insurance-for-families-indianapolis', label: 'Best Life Insurance for Indianapolis Families' },
    { href: '/blog/life-insurance-quotes-indiana', label: 'Life Insurance Quotes Indiana' },
    { href: '/blog/life-insurance-indiana-seniors', label: 'Life Insurance for Seniors in Indiana' },
    { href: '/blog/no-medical-exam-life-insurance-indiana', label: 'No Medical Exam Life Insurance Indiana' },
    { href: '/blog/life-insurance-self-employed-indiana', label: 'Life Insurance for Self-Employed Indiana' },
    { href: '/blog/burial-insurance-indiana', label: 'Burial Insurance Indiana' },
    { href: '/blog/life-insurance-smokers-indiana', label: 'Life Insurance for Smokers Indiana' },
    { href: '/blog/life-insurance-stay-at-home-parents-indiana', label: 'Life Insurance for Stay-at-Home Parents Indiana' },
    { href: '/blog/life-insurance-checklist-indiana', label: 'Life Insurance Checklist Indiana' },
  ];

  const resourceArticles = [
    { href: '/resources', label: 'Resources Hub — All Guides' },
    { href: '/learning-hub', label: 'Life Insurance Learning Hub — 27 Indiana Guides' },
    { href: '/resources/term-vs-whole-life-indianapolis-homeowners', label: 'Term vs Whole Life for Indianapolis Homeowners' },
    { href: '/resources/mortgage-protection-fishers-family-story', label: 'Mortgage Protection: A Fishers Family Story' },
    { href: '/resources/life-insurance-mistakes-indiana-families', label: 'Life Insurance Mistakes Indiana Families Make' },
    { href: '/resources/how-much-life-insurance-indiana-families-need', label: 'How Much Life Insurance Do Indiana Families Need?' },
    { href: '/resources/final-expense-insurance-indiana-seniors', label: 'Final Expense Insurance for Indiana Seniors' },
    { href: '/resources/annuities-retirement-planning-indiana', label: 'Annuities & Retirement Planning in Indiana' },
    { href: '/resources/life-insurance-for-smokers-indiana', label: 'Life Insurance for Smokers in Indiana' },
    { href: '/resources/life-insurance-for-business-owners-indiana', label: 'Life Insurance for Business Owners in Indiana' },
    { href: '/resources/no-medical-exam-life-insurance-indiana-guide', label: 'No Medical Exam Life Insurance Indiana Guide' },
    { href: '/resources/life-insurance-for-seniors-indiana', label: 'Life Insurance for Seniors in Indiana' },
    { href: '/resources/life-insurance-riders-indiana', label: 'Life Insurance Riders in Indiana — Complete Guide' },
    { href: '/resources/how-to-choose-life-insurance-company-indiana', label: 'How to Choose a Life Insurance Company in Indiana' },
    { href: '/resources/indiana-life-insurance-calculator', label: 'Indiana Life Insurance Needs Calculator' },
    { href: '/resources/life-insurance-glossary-indiana', label: 'Life Insurance Glossary — 55+ Terms Defined' },
    { href: '/resources/life-insurance-checklist-indiana', label: 'Indiana Life Insurance Buying Checklist — 35 Steps' },
  ];

  const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="bg-white rounded-xl p-7 border border-gray-100">
      <h2 className="text-[18px] font-bold text-[#1A3A52] mb-5 pb-3 border-b border-gray-100">{title}</h2>
      {children}
    </section>
  );

  const LinkList = ({ links, cols = 1 }: { links: { href: string; label: string }[]; cols?: number }) => (
    <ul className={`grid gap-y-2.5 gap-x-6 ${cols === 2 ? 'grid-cols-1 sm:grid-cols-2' : cols === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
      {links.map((link) => (
        <li key={link.href}>
          {/* Plain <a href> — intentional for raw HTML crawler discoverability */}
          <a
            href={link.href}
            className="flex items-center gap-2 text-[14px] text-[#D97642] hover:text-[#C26638] hover:underline transition-colors group"
          >
            <i className="ri-arrow-right-s-line text-[#D97642]/50 group-hover:text-[#D97642] text-[15px] flex-shrink-0"></i>
            {link.label}
          </a>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="min-h-screen bg-[#F9F7F4]">
      <title>HTML Sitemap | Hoosier Life Insurance — All Pages Directory</title>
      <Header />

      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#D97642]/10 border border-[#D97642]/20 rounded-full px-4 py-1.5 mb-4">
              <i className="ri-map-2-line text-[#D97642] text-[13px]"></i>
              <span className="text-[#D97642] text-[12px] font-semibold uppercase tracking-widest">Complete Site Directory</span>
            </div>
            <h1 className="text-[36px] md:text-[44px] font-bold text-[#1A3A52] mb-4">HTML Sitemap</h1>
            <p className="text-[16px] text-slate-500 max-w-2xl mx-auto">
              Browse every page on Hoosier Life Insurance — products, Indiana service areas, blog articles, resources, and tools.
            </p>
          </div>

          {/* Top-level grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <SectionCard title="Core Pages">
              <LinkList links={corePages} />
            </SectionCard>

            <SectionCard title="Insurance Products & Strategies">
              <LinkList links={productPages} />
            </SectionCard>
          </div>

          {/* Location pages */}
          <div className="mb-6">
            <SectionCard title="Location-Specific Pages">
              <LinkList links={locationPages} cols={2} />
            </SectionCard>
          </div>

          {/* Specialty pages — full width 3-col */}
          <div className="mb-6">
            <SectionCard title="Specialty & High-Intent Pages">
              <LinkList links={specialtyPages} cols={3} />
            </SectionCard>
          </div>

          {/* Service areas */}
          <div className="mb-6">
            <SectionCard title="Service Areas — All Indiana Cities">
              <LinkList links={serviceAreas} cols={3} />
            </SectionCard>
          </div>

          {/* Blog + Resources side by side */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <SectionCard title="Blog Articles">
              <LinkList links={blogArticles} />
            </SectionCard>

            <SectionCard title="Resource Guides & Tools">
              <LinkList links={resourceArticles} />
            </SectionCard>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-10">
            <a
              href="/"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-white transition-all hover:opacity-90 whitespace-nowrap"
              style={{ backgroundColor: '#D97642' }}
            >
              <i className="ri-home-line"></i>
              Back to Homepage
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}